import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for API responses
export interface RestaurantListItem {
  id: string;
  name: string;
  location?: string;
  rating: number;
  orders: number;
  revenue: number;
  status: "active" | "inactive" | "suspended";
  action: string;
}

export interface SuspendedRestaurantItem {
  id: string;
  name: string;
  suspensionReason: string;
  suspensionDate: string;
  walletBalance: number;
  status: "suspended";
}

export interface RestaurantInfo {
  id: string;
  name: string;
  email: string;
  type: string;
  managerName: string;
  isOpen: boolean;
  freeDelivery: boolean;
  rating: number;
}

export interface RestaurantCounts {
  totalCategories: number;
  totalMeals: number;
  totalOrders: number;
  balance: number;
  totalReviews: number;
}

export interface RestaurantDetails {
  restaurantInfo: RestaurantInfo;
  counts: RestaurantCounts;
}

export interface DocumentStatus {
  type: string;
  url: string | null;
  status: "pending" | "approved" | "rejected";
}

export interface RestaurantDocuments {
  restaurantId: string;
  restaurantName: string;
  documents: {
    cacRegistrationCertificate: DocumentStatus;
    foodLicense: DocumentStatus;
    taxIdentificationNumber: DocumentStatus;
  };
}

export interface RestaurantBankInfo {
  restaurantId: string;
  restaurantName: string;
  bankAccounts: any[];
  totalAccounts: number;
}

// API Response interfaces
interface RestaurantListResponse {
  success: boolean;
  data: RestaurantListItem[];
  total: number;
}

interface RestaurantDetailsResponse {
  success: boolean;
  data: RestaurantDetails;
}

interface RestaurantDocumentsResponse {
  success: boolean;
  data: RestaurantDocuments;
}

interface RestaurantBankInfoResponse {
  success: boolean;
  data: RestaurantBankInfo;
}

interface SuspendedRestaurantsResponse {
  success: boolean;
  data: SuspendedRestaurantItem[];
  total: number;
}

// Redux state interface
interface RestaurantsState {
  restaurants: RestaurantListItem[];
  suspendedRestaurants: SuspendedRestaurantItem[];
  currentRestaurant: RestaurantDetails | null;
  currentRestaurantDocuments: RestaurantDocuments | null;
  currentRestaurantBankInfo: RestaurantBankInfo | null;
  status: "idle" | "loading" | "success" | "failed";
  suspendedStatus: "idle" | "loading" | "success" | "failed";
  detailsStatus: "idle" | "loading" | "success" | "failed";
  documentsStatus: "idle" | "loading" | "success" | "failed";
  bankInfoStatus: "idle" | "loading" | "success" | "failed";
  error: string | null;
  total: number;
  suspendedTotal: number;
}

const initialState: RestaurantsState = {
  restaurants: [],
  suspendedRestaurants: [],
  currentRestaurant: null,
  currentRestaurantDocuments: null,
  currentRestaurantBankInfo: null,
  status: "idle",
  suspendedStatus: "idle",
  detailsStatus: "idle",
  documentsStatus: "idle",
  bankInfoStatus: "idle",
  error: null,
  total: 0,
  suspendedTotal: 0,
};

// Async thunks for API calls
export const getAllRestaurants = createAsyncThunk<
  RestaurantListResponse,
  { location?: string } | void,
  { rejectValue: string }
>("restaurants/getAllRestaurants", async (params, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!token) {
      return rejectWithValue("Authentication token not found");
    }

    const baseUrl = `${process.env.REACT_APP_RESTAURANT_URL}/restaurants`;
    const location = (params as any)?.location?.trim();
    const url = location ? `${baseUrl}?location=${encodeURIComponent(location)}` : baseUrl;

    const response = await axios.get<RestaurantListResponse>(
      url,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch restaurants"
    );
  }
});

export const getRestaurantDetails = createAsyncThunk<
  RestaurantDetailsResponse,
  string,
  { rejectValue: string }
>(
  "restaurants/getRestaurantDetails",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantDetailsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurants/${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant details"
      );
    }
  }
);

export const getRestaurantDocuments = createAsyncThunk<
  RestaurantDocumentsResponse,
  string,
  { rejectValue: string }
>(
  "restaurants/getRestaurantDocuments",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantDocumentsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurants/${restaurantId}/documents`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant documents"
      );
    }
  }
);
export const getRestaurantBankInfo = createAsyncThunk<
  RestaurantBankInfoResponse,
  string,
  { rejectValue: string }
>(
  "restaurants/getRestaurantBankInfo",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantBankInfoResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurants/${restaurantId}/bank-info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant bank info"
      );
    }
  }
);

// Interface for the actual API response format
interface SuspendedRestaurantApiResponse {
  restaurantGeneratedID: string;
  currentBalance: number;
  suspensionReason: string;
  suspensionDate: string;
}

interface SuspendedRestaurantsApiResponse {
  success?: boolean;
  data?: SuspendedRestaurantApiResponse[];
  total?: number;
}

export const getSuspendedRestaurants = createAsyncThunk<
  SuspendedRestaurantsResponse,
  void,
  { rejectValue: string }
>("restaurants/getSuspendedRestaurants", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!token) {
      return rejectWithValue("Authentication token not found");
    }

    const response = await axios.get(
      `${process.env.REACT_APP_RESTAURANT_URL}/suspended`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    // Handle different response formats
    let apiData: SuspendedRestaurantApiResponse[] = [];
    let total = 0;
    
    if (Array.isArray(response.data)) {
      // Direct array response
      apiData = response.data;
      total = apiData.length;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // Wrapped response with data property
      apiData = response.data.data;
      total = response.data.total || apiData.length;
    } else {
      throw new Error("Unexpected response format");
    }
    
    // Transform API response to match our interface
    const transformedData: SuspendedRestaurantItem[] = apiData.map(item => ({
      id: item.restaurantGeneratedID,
      name: item.restaurantGeneratedID, // Using ID as name since name is not provided
      suspensionReason: item.suspensionReason,
      suspensionDate: new Date(item.suspensionDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      }),
      walletBalance: item.currentBalance,
      status: "suspended" as const
    }));
    
    return {
      success: true,
      data: transformedData,
      total: total
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch suspended restaurants"
    );
  }
});

// Redux slice
const restaurantsSlice = createSlice({
  name: "restaurants",
  initialState,
  reducers: {
    clearCurrentRestaurant: (state) => {
      state.currentRestaurant = null;
      state.currentRestaurantDocuments = null;
      state.currentRestaurantBankInfo = null;
      state.detailsStatus = "idle";
      state.documentsStatus = "idle";
      state.bankInfoStatus = "idle";
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all restaurants
      .addCase(getAllRestaurants.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllRestaurants.fulfilled, (state, action) => {
        state.status = "success";
        state.restaurants = action.payload.data;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(getAllRestaurants.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch restaurants";
      })

      // Get restaurant details
      .addCase(getRestaurantDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;
      })
      .addCase(getRestaurantDetails.fulfilled, (state, action) => {
        state.detailsStatus = "success";
        state.currentRestaurant = action.payload.data;
        state.error = null;
      })
      .addCase(getRestaurantDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.error = action.payload || "Failed to fetch restaurant details";
      })

      // Get restaurant documents
      .addCase(getRestaurantDocuments.pending, (state) => {
        state.documentsStatus = "loading";
        state.error = null;
      })
      .addCase(getRestaurantDocuments.fulfilled, (state, action) => {
        state.documentsStatus = "success";
        state.currentRestaurantDocuments = action.payload.data;
        state.error = null;
      })
      .addCase(getRestaurantDocuments.rejected, (state, action) => {
        state.documentsStatus = "failed";
        state.error = action.payload || "Failed to fetch restaurant documents";
      })

      // Get restaurant bank info
      .addCase(getRestaurantBankInfo.pending, (state) => {
        state.bankInfoStatus = "loading";
        state.error = null;
      })
      .addCase(getRestaurantBankInfo.fulfilled, (state, action) => {
        state.bankInfoStatus = "success";
        state.currentRestaurantBankInfo = action.payload.data;
        state.error = null;
      })
      .addCase(getRestaurantBankInfo.rejected, (state, action) => {
        state.bankInfoStatus = "failed";
        state.error = action.payload || "Failed to fetch restaurant bank info";
      })

      // Get suspended restaurants
      .addCase(getSuspendedRestaurants.pending, (state) => {
        state.suspendedStatus = "loading";
        state.error = null;
      })
      .addCase(getSuspendedRestaurants.fulfilled, (state, action) => {
        state.suspendedStatus = "success";
        state.suspendedRestaurants = action.payload.data;
        state.suspendedTotal = action.payload.total;
        state.error = null;
      })
      .addCase(getSuspendedRestaurants.rejected, (state, action) => {
        state.suspendedStatus = "failed";
        state.error = action.payload || "Failed to fetch suspended restaurants";
      });
  },
});

export const { clearCurrentRestaurant, clearErrors } = restaurantsSlice.actions;
export default restaurantsSlice.reducer;
